import { useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import type { SurpriseScore } from "@macroscope/shared";
import { MAJOR_ECONOMIES } from "@macroscope/shared";
import { fetchSurpriseScores } from "../lib/api";

export default function SurpriseEngine() {
  const [flavor, setFlavor] = useState<"trend" | "consensus">("trend");
  const { data, isLoading } = useQuery({
    queryKey: ["surprise", flavor],
    queryFn: () => fetchSurpriseScores(flavor),
  });

  if (isLoading) return <div className="text-sm text-slate-500">Loading Surprise Engine…</div>;

  const scores = data?.scores ?? [];
  const global = scores.find((s) => s.scope === "GLOBAL");
  const countries = scores.filter((s) => s.scope !== "GLOBAL");

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold">Surprise Engine</h1>
            <p className="text-xs text-slate-500">
              {flavor === "consensus"
                ? "Economic-release surprise scores vs. consensus (where free data exists)."
                : "Economic-release surprise scores vs. trend expectation."}
            </p>
            {data?.coverage_gap && (
              <p className="mt-1 text-xs text-yellow-500">{data.coverage_gap}</p>
            )}
          </div>
          <FlavorToggle value={flavor} onChange={setFlavor} />
        </div>
      </section>

      {global && <GlobalGauge score={global} />}

      <section className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-surface-2 bg-surface-1 p-5">
            <h2 className="mb-4 text-xs font-medium uppercase tracking-wider text-slate-400">Country heatmap</h2>
            <CountryHeatmap countries={countries} />
          </div>
        </div>
        <div className="rounded-xl border border-surface-2 bg-surface-1 p-5">
          <h2 className="mb-4 text-xs font-medium uppercase tracking-wider text-slate-400">Top contributors</h2>
          <ContributorsList scores={countries} />
        </div>
      </section>

      <div className="text-[10px] text-slate-600">
        Source: {data?.source} · {data?.updated_at ? new Date(data.updated_at).toLocaleString() : ""}
        {data?.delayed && " · delayed"} · educational
      </div>
    </div>
  );
}

function FlavorToggle({ value, onChange }: { value: "trend" | "consensus"; onChange: (v: "trend" | "consensus") => void }) {
  return (
    <div className="flex rounded-lg border border-surface-2 bg-surface-1 p-1">
      {(["trend", "consensus"] as const).map((f) => (
        <button
          key={f}
          onClick={() => onChange(f)}
          className={`rounded px-3 py-1 text-xs font-medium capitalize ${
            value === f ? "bg-macro-accent/10 text-macro-accent" : "text-slate-400 hover:text-slate-200"
          }`}
        >
          {f}
        </button>
      ))}
    </div>
  );
}

function GlobalGauge({ score }: { score: SurpriseScore }) {
  const pct = Math.max(-100, Math.min(100, score.score));
  const label = pct > 25 ? "positive surprise bias" : pct < -25 ? "negative surprise bias" : "neutral surprise bias";
  return (
    <div className="rounded-xl border border-surface-2 bg-surface-1 p-5">
      <h2 className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-400">Global surprise gauge</h2>
      <div className="flex items-end gap-3">
        <div className="font-mono text-4xl font-semibold">{pct > 0 ? "+" : ""}
          {pct.toFixed(1)}</div>
        <div className="pb-1 text-sm text-slate-400">{label}</div>
      </div>
      <div className="mt-3 h-2 w-full rounded-full bg-surface-2">
        <div
          className={`h-2 rounded-full ${pct >= 0 ? "bg-macro-up" : "bg-macro-down"}`}
          style={{ width: `${Math.abs(pct)}%`, marginLeft: pct >= 0 ? "50%" : `${50 - Math.abs(pct)}%` }}
        />
      </div>
      <p className="mt-3 text-xs text-slate-500">
        so-what: {pct > 25 ? "Data has mostly beaten trend — growth/inflation momentum may be firmer than priced." : pct < -25 ? "Data has mostly missed trend — momentum may be softer than priced." : "Releases are mixed vs. trend — no clear macro surprise edge."}
      </p>
    </div>
  );
}

function CountryHeatmap({ countries }: { countries: SurpriseScore[] }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {MAJOR_ECONOMIES.map((code) => {
        const score = countries.find((s) => s.scope === code);
        const value = score?.score ?? 0;
        return (
          <Link
            key={code}
            href={`/surprise/${code}`}
            className="rounded-lg border border-surface-2 bg-surface-2 p-4 transition-colors hover:border-macro-accent/30"
          >
            <div className="text-xs font-medium uppercase tracking-wider text-slate-400">{code}</div>
            <div className={`mt-1 font-mono text-xl font-semibold ${value > 0 ? "text-macro-up" : value < 0 ? "text-macro-down" : "text-slate-300"}`}>
              {value > 0 ? "+" : ""}
              {value.toFixed(1)}
            </div>
            <div className="mt-1 text-[10px] text-slate-500">{score ? `${score.contributors.length} events` : "no data"}</div>
          </Link>
        );
      })}
    </div>
  );
}

function ContributorsList({ scores }: { scores: SurpriseScore[] }) {
  const allContributors = scores
    .flatMap((s) => s.contributors.map((c) => ({ ...c, scope: s.scope })))
    .sort((a, b) => Math.abs(b.z) - Math.abs(a.z))
    .slice(0, 10);

  if (allContributors.length === 0) {
    return <div className="text-sm text-slate-500">No scored releases in the current window.</div>;
  }

  return (
    <ul className="space-y-2">
      {allContributors.map((c, i) => (
        <li key={`${c.event_id}-${i}`} className="rounded-lg border border-surface-2 bg-surface-2 px-3 py-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase text-slate-400">{c.scope}</span>
            <span className={`text-xs font-mono ${c.z > 0 ? "text-macro-up" : "text-macro-down"}`}>
              {c.z > 0 ? "+" : ""}
              {c.z.toFixed(2)}σ
            </span>
          </div>
          <div className="mt-1 text-sm">{c.event_id}</div>
          <div className="text-[10px] text-slate-500">weight {(c.weight * 100).toFixed(0)}%</div>
        </li>
      ))}
    </ul>
  );
}
